import {
  boardRectToScreen,
  boardToScreen,
  nodeAABB,
  vDist,
  type NodeId,
  type NodeLookup,
  type Presence,
  type Vec,
  type Viewport,
} from '@kanvas/board-core';
import { DURATION, prefersReducedMotion, smoothTowards } from './motion';

/**
 * Remote cursors and remote selections.
 *
 * Presence arrives at network rate — a peer's `pointermove` is throttled before
 * it is published, and the packets then queue behind each other. Painting those
 * positions directly makes every other cursor stutter, which reads as lag in
 * the *app* rather than in the network. So each peer gets a locally smoothed
 * position that eases towards the last received one, advanced once per frame.
 *
 * The same reasoning covers opacity: cursors fade in when a peer arrives, dim
 * when they go idle, and fade out when they leave, instead of popping.
 */

/** Time constant for cursor smoothing. Lower feels snappier but jitters more. */
const SMOOTHING_MS = 60;
/** How long a peer can sit still before their cursor dims. */
const IDLE_MS = 6000;
const IDLE_OPACITY = 0.4;
/** Below this the peer is dropped entirely. */
const GONE_EPSILON = 0.01;

interface PeerCursor {
  userId: string;
  name: string;
  color: string;
  tool: string;
  /** Smoothed board position actually painted. */
  current: Vec;
  /** Last position received from the peer. */
  target: Vec;
  opacity: number;
  targetOpacity: number;
  /** When the target last actually changed, for the idle fade. */
  lastMoved: number;
  /** When the peer first appeared, for the entrance fade. */
  appeared: number;
  selection: NodeId[];
  speaking: boolean;
}

export class PeerCursorLayer {
  private peers = new Map<string, PeerCursor>();
  private lastTick = 0;

  /** Folds the latest presence snapshot into the animated state. */
  sync(presence: readonly Presence[], now: number): void {
    const seen = new Set<string>();

    for (const peer of presence) {
      seen.add(peer.userId);
      const existing = this.peers.get(peer.userId);

      if (!peer.cursor) {
        // Pointer left their canvas: fade out but keep the entry so the fade
        // can finish, and so a quick return does not re-run the entrance.
        if (existing) existing.targetOpacity = 0;
        continue;
      }

      if (!existing) {
        this.peers.set(peer.userId, {
          userId: peer.userId,
          name: peer.name,
          color: peer.color,
          tool: peer.tool,
          current: { ...peer.cursor },
          target: { ...peer.cursor },
          opacity: prefersReducedMotion() ? 1 : 0,
          targetOpacity: 1,
          lastMoved: now,
          appeared: now,
          selection: peer.selection,
          speaking: peer.speaking ?? false,
        });
        continue;
      }

      if (vDist(existing.target, peer.cursor) > 0.01) {
        existing.target = { ...peer.cursor };
        existing.lastMoved = now;
      }
      existing.name = peer.name;
      existing.color = peer.color;
      existing.tool = peer.tool;
      existing.selection = peer.selection;
      existing.speaking = peer.speaking ?? false;
      existing.targetOpacity = now - existing.lastMoved > IDLE_MS ? IDLE_OPACITY : 1;
    }

    // Peers no longer in the presence set have disconnected.
    for (const [id, cursor] of this.peers) {
      if (!seen.has(id)) cursor.targetOpacity = 0;
    }
  }

  /**
   * Advances the smoothing. Returns true while anything is still moving, so
   * the renderer knows to schedule another frame.
   */
  tick(now: number): boolean {
    const deltaMs = this.lastTick === 0 ? 16 : Math.min(now - this.lastTick, 64);
    this.lastTick = now;

    let animating = false;

    for (const [id, cursor] of this.peers) {
      const nextX = smoothTowards(cursor.current.x, cursor.target.x, deltaMs, SMOOTHING_MS);
      const nextY = smoothTowards(cursor.current.y, cursor.target.y, deltaMs, SMOOTHING_MS);
      const moving = nextX !== cursor.current.x || nextY !== cursor.current.y;
      cursor.current = { x: nextX, y: nextY };

      const nextOpacity = smoothTowards(
        cursor.opacity,
        cursor.targetOpacity,
        deltaMs,
        DURATION.fast / 3,
      );
      const fading = nextOpacity !== cursor.opacity;
      cursor.opacity = nextOpacity;

      if (cursor.targetOpacity === 0 && cursor.opacity < GONE_EPSILON) {
        this.peers.delete(id);
        continue;
      }

      // Re-check idleness here too: a peer that stops moving never sends
      // another packet, so `sync` alone would never notice they went quiet.
      if (cursor.targetOpacity !== 0) {
        cursor.targetOpacity = now - cursor.lastMoved > IDLE_MS ? IDLE_OPACITY : 1;
      }

      if (moving || fading) animating = true;
    }

    return animating;
  }

  /** Peer-coloured outlines around whatever each peer has selected. */
  paintSelections(ctx: CanvasRenderingContext2D, vp: Viewport, lookup: NodeLookup): void {
    for (const cursor of this.peers.values()) {
      if (cursor.selection.length === 0 || cursor.opacity < 0.1) continue;
      // Cap the work: a peer with Select-All active must not cost a frame.
      const ids = cursor.selection.slice(0, 60);

      ctx.save();
      ctx.globalAlpha = cursor.opacity * 0.9;
      ctx.strokeStyle = cursor.color;
      ctx.lineWidth = 1.5;
      ctx.setLineDash([]);

      for (const id of ids) {
        const node = lookup(id);
        if (!node) continue;
        const r = boardRectToScreen(vp, nodeAABB(node, lookup));
        if (r.x > vp.width || r.y > vp.height || r.x + r.w < 0 || r.y + r.h < 0) continue;
        roundRect(ctx, r.x - 1.5, r.y - 1.5, r.w + 3, r.h + 3, 3);
        ctx.stroke();
      }
      ctx.restore();
    }
  }

  paintCursors(ctx: CanvasRenderingContext2D, vp: Viewport, now: number): void {
    ctx.save();
    ctx.font = '600 11px Inter, system-ui, sans-serif';
    ctx.textBaseline = 'middle';

    for (const cursor of this.peers.values()) {
      if (cursor.opacity < 0.02) continue;
      const p = boardToScreen(vp, cursor.current);
      // Off-screen peers are the edge chips in `OffscreenPeerHints`, not a
      // cursor painted in empty space past the canvas.
      if (p.x < -8 || p.y < -8 || p.x > vp.width + 8 || p.y > vp.height + 8) continue;

      // The entrance scales the whole cursor up from small, which reads as
      // "someone arrived" far more clearly than opacity alone.
      const age = now - cursor.appeared;
      const intro = prefersReducedMotion() ? 1 : Math.min(1, age / DURATION.normal);
      const scale = 0.6 + 0.4 * (1 - (1 - intro) ** 3);

      ctx.save();
      ctx.globalAlpha = cursor.opacity;
      ctx.translate(p.x, p.y);
      ctx.scale(scale, scale);

      if (cursor.speaking) paintSpeakingRing(ctx, cursor.color, now);
      paintCursorGlyph(ctx, cursor.tool, cursor.color);
      paintNameChip(ctx, cursor.name, cursor.color);

      ctx.restore();
    }
    ctx.restore();
  }

  clear(): void {
    this.peers.clear();
    this.lastTick = 0;
  }
}

// ---------------------------------------------------------------------------
// Glyphs
// ---------------------------------------------------------------------------

const ARROW = 'M0 0 L0 16.5 L4.2 12.6 L6.9 18.6 L9.6 17.4 L6.9 11.4 L12.6 11.1 Z';
/** A pen nib, shown while a peer is drawing so their intent is obvious. */
const NIB = 'M0 0 L11.5 4.4 L4.4 11.5 Z';

let arrowPath: Path2D | null = null;
let nibPath: Path2D | null = null;

function paintCursorGlyph(
  ctx: CanvasRenderingContext2D,
  tool: string,
  color: string,
): void {
  if (!arrowPath) arrowPath = new Path2D(ARROW);
  if (!nibPath) nibPath = new Path2D(NIB);

  ctx.fillStyle = color;
  ctx.strokeStyle = 'rgba(255,255,255,0.92)';
  ctx.lineWidth = 1.2;

  if (tool === 'eraser') {
    // An outlined ring, matching the eraser's own on-canvas affordance.
    ctx.beginPath();
    ctx.arc(7, 7, 7, 0, Math.PI * 2);
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.stroke();
    return;
  }

  const path = tool === 'draw' || tool === 'laser' ? nibPath : arrowPath;
  ctx.fill(path);
  ctx.stroke(path);
}

function paintNameChip(ctx: CanvasRenderingContext2D, name: string, color: string): void {
  const width = ctx.measureText(name).width + 14;
  ctx.save();
  ctx.translate(14, 16);
  ctx.fillStyle = color;
  roundRect(ctx, 0, 0, width, 19, 9.5);
  ctx.fill();
  ctx.fillStyle = '#FFFFFF';
  ctx.fillText(name, 7, 10);
  ctx.restore();
}

/** Pulsing halo while the peer is talking in the call attached to this board. */
function paintSpeakingRing(
  ctx: CanvasRenderingContext2D,
  color: string,
  now: number,
): void {
  const phase = prefersReducedMotion() ? 0.5 : (now % 1400) / 1400;
  const radius = 10 + phase * 14;
  ctx.save();
  ctx.globalAlpha *= (1 - phase) * 0.5;
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(6, 8, radius, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  if (typeof ctx.roundRect === 'function') {
    ctx.roundRect(x, y, w, h, r);
    return;
  }
  const rad = Math.min(r, w / 2, h / 2);
  ctx.moveTo(x + rad, y);
  ctx.arcTo(x + w, y, x + w, y + h, rad);
  ctx.arcTo(x + w, y + h, x, y + h, rad);
  ctx.arcTo(x, y + h, x, y, rad);
  ctx.arcTo(x, y, x + w, y, rad);
  ctx.closePath();
}
